import {
  deleteRequest,
  getRequest,
  postFormData,
  postRequest,
  putRequest,
} from '.'
import CustomDomainHostStatus from 'app/frontend/types/custom/CustomDomainHostStatuses'

export const getGuildDetails = async (id: number): Promise<wowaudit.Guild> => {
  return await getRequest(`/api/guilds/${id}`)
}

export const getGuildDetailsByPath = async (
  path: string,
): Promise<wowaudit.Guild> => {
  return await getRequest(`/api/guilds/search?path=${encodeURIComponent(path)}`)
}

export const getGuildMembers = async (
  id: number,
): Promise<wowaudit.CharacterReference[]> => {
  return await getRequest(`/api/guilds/${id}/characters`)
}

export const getGuildUsers = async (
  id: number,
): Promise<wowaudit.UserReference[]> => {
  return await getRequest(`/api/guilds/${id}/users`)
}

export const getGuildNewsItems = async (
  guildId: number,
): Promise<wowaudit.NewsItem[]> => {
  return await getRequest(`/api/guilds/${guildId}/news_items`)
}

export const getGuildNewsItem = async (
  guildId: number,
  id: number,
): Promise<wowaudit.NewsItem> => {
  return await getRequest(`/api/guilds/${guildId}/news_items/${id}`)
}

export const getGuildOverviewData = async (
  guildId: number,
): Promise<wowaudit.OverviewData> => {
  return await getRequest(`/api/guilds/${guildId}/overview_data`)
}

export type GuildTeamData = Record<
  number,
  {
    roster: wowaudit.TeamMembership[]
    medianItemLevel?: number | null
    eventDays: string[]
    raidProgress?: NonNullable<wowaudit.OverviewData['raidProgress']>
  }
>

export const getGuildTeamData = async (
  guildId: number,
): Promise<GuildTeamData> => {
  return await getRequest(`/api/guilds/${guildId}/team_data`)
}

export const getGuildLinkCandidates = async (
  guildId: number,
): Promise<wowaudit.LinkedGuild[]> => {
  return await getRequest(`/api/guilds/${guildId}/links/candidates`)
}

export const validateGuildCustomDomainHost = async (
  guildId: number,
  params: {
    host?: string
    subdomain?: string
  },
): Promise<{ status: CustomDomainHostStatus }> => {
  return await postRequest(
    `/api/guilds/${guildId}/custom_domain_hosts/validate`,
    params,
  )
}

export const deleteGuildCustomDomainHost = async (
  guildId: number,
  id: number,
): Promise<wowaudit.Guild> => {
  return await deleteRequest(`/api/guilds/${guildId}/custom_domain_hosts/${id}`)
}

export const refreshGuildCustomDomainHost = async (
  guildId: number,
  id: number,
): Promise<wowaudit.Guild> => {
  return await postRequest(
    `/api/guilds/${guildId}/custom_domain_hosts/${id}/refresh`,
  )
}

export const uploadGuildFile = async (
  guildId: number,
  file: File,
): Promise<{ url: string }> => {
  const formData = new FormData()
  formData.append('file', file)
  return await postFormData(`/api/guilds/${guildId}/files`, formData)
}

export const updateGuild = async (id: number): Promise<{ job_id: string }> => {
  return await putRequest(`/api/guilds/${id}`, {})
}

export const updateLinkedGuilds = async (
  id: number,
  linkedGuildIds: number[],
): Promise<wowaudit.Guild> => {
  return await putRequest(`/api/guilds/${id}`, { guild: { linkedGuildIds } })
}

export const reactivateGuild = async (id: number): Promise<wowaudit.Guild> => {
  return await putRequest(`/api/guilds/${id}/reactivate`, {})
}

export const transferGuild = async (
  id: number,
  data: { realm: wowaudit.Realm; name: string },
): Promise<{ job_id: string }> => {
  return await postRequest(`/api/guilds/${id}/transfer`, data)
}

export const createManualGuild = async (
  realmId: number,
  name: string,
): Promise<wowaudit.GuildReference> => {
  return await postRequest('/api/guilds/manual', { realmId, name })
}
